// @ts-ignore  - no types for this library without going up react router version...
import xhr from 'xhr';
// @ts-ignore - initial conversion to TS
import Config from 'Config';
import retry from 'retry';
import safeStringify from 'fast-safe-stringify';

import Cookies from 'lib/Cookies';
// @ts-ignore until we can do node run TS
import userAgent from 'lib/currentUserAgent';
import uuid from 'uuid/v4';

import { RouterState } from 'lib/ReactRouterTypes';

declare var __DEV__: string;

const ENABLED = Config.Environment.R101_EVENTS_ENABLED;
const INTERVAL = Config.Environment.R101_EVENTS_INTERVAL;
const URL = Config.Environment.R101_EVENTS_URL;

function decamelizeProperties(properties: { [key: string]: any }) {
  const props: { [key: string]: any } = {};
  for (const key in properties) {
    props[key.replace(/([a-z\d])([A-Z])/g, '$1_$2').toLowerCase()] = properties[key];
  }
  return props;
}

const windowDefined = typeof window !== 'undefined';

export const ref = { xhr }; // tests :(

function dispatchBuffer(
  events: EventData[],
  properties: any,
  onError: (unsentEvents: EventData[]) => void
) {
  const rawData = {
    batch: events,
    properties,
  };
  const data = safeStringify(rawData);

  const operation = retry.operation({
    retries: 5,
    factor: 2, // exponential rollback
    minTimeout: 500,
    maxTimeout: 1000 * 60,
  });

  operation.attempt(function() {
    ref.xhr(
      // @ts-ignore - initial conversion to TS
      {
        url: URL + '/v1/import',
        method: 'POST',
        useXDR: true,
        data,
      },
      function(err: any, res: any) {
        if (!res || res.statusCode >= 500) {
          const outOfRetries = !operation.retry(err);
          if (outOfRetries && err) {
            onError(events);
            console.warn('eventTracking failed to flush', operation.mainError(), res);
          }
        }
      }
    );
  });

  // Log bad event data, such as circular references.
  try {
    JSON.stringify(rawData);
  } catch (e) {
    console.error('Failed to serialize event data: ', data);
  }
}

type EventData = {
  event: string;
  timestamp: string;
  properties?: Object;
  client_type?: string;
  client_uuid?: string;
  foreign_service?: string;
  request_url?: string;
  screen_width?: number;
  screen_height?: number;
  session_uuid?: string;
};

export class EventTracking {
  doPush: boolean;
  buffer: EventData[];
  timeout?: NodeJS.Timer | null;
  userId?: number | null;
  userUuid?: string | null;
  clientVersion?: string | null;
  properties: {};
  batchProperties: {};

  constructor(doPush: boolean) {
    this.doPush = doPush;
    this.buffer = [];
    this.timeout = null;
    this.userId = null;
    this.userUuid = null;
    this.clientVersion = Config.Environment.EMPIRE_RELEASE;
    if (doPush && windowDefined) {
      window.addEventListener('unload', this.flushBuffer.bind(this), false);
    }
    this.properties = {};
    this.batchProperties = {};
  }

  setUserInfo(userId?: number, userUuid?: string) {
    this.userId = userId;
    this.userUuid = userUuid;
  }

  addBatchProperties(batchProperties: {}) {
    this.batchProperties = { ...this.batchProperties, ...batchProperties };
  }

  getBatchProperties() {
    if (!windowDefined) {
      // TODO handle this more smartly in SSR
      return {};
    }

    return {
      client_segment: userAgent.clientSegment(),
      client_type: 'Web',
      client_uuid: this.getClientId(),
      foreign_service: 'r101-web-client',
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      session_uuid: this.getSessionId(),
      client_version: this.clientVersion,
      ...this.batchProperties,
    };
  }

  /**
   * SessionId uniquely identifies a session. A session is
   * begins when a user hits remind.com, and terminates when
   * the browser closes or the user logs out.
   *
   * I.e. this is a *usage* session *not an auth* session.
   */
  getSessionId(): string | undefined {
    if (!windowDefined) {
      return undefined;
    }

    const storedSessionId = Cookies.get('session_uuid');
    if (storedSessionId) {
      return storedSessionId;
    }

    const freshSessionId = uuid();
    Cookies.set('session_uuid', freshSessionId);
    return freshSessionId;
  }

  getClientId(): string | undefined {
    if (windowDefined) {
      let clientId = Cookies.get('client_uuid');
      if (!clientId) {
        Cookies.set('client_uuid', (clientId = uuid()));
      }
      return clientId;
    }
    return undefined;
  }

  // Used internally - generally use trackKey for ui tracking.
  track(name: string, input_properties: any = {}) {
    const properties = {
      ...input_properties,
      ...this.properties,
    };

    properties['user_id'] = this.userId;
    properties['user_uuid'] = this.userUuid;
    if (windowDefined) {
      properties['request_url'] = window.location.href;
    }

    if (!this.doPush) {
      if (__DEV__) {
        const tag = properties.event_key ? properties.event_key : name;
        console.groupCollapsed(`%c⚡️ EVT - ${tag}`, 'color: #962857');
        console.log(properties);
        console.log({ batch_properties: this.getBatchProperties() });
        console.groupEnd();
      }

      return;
    }

    this.buffer.push({
      event: name,
      properties,
      timestamp: new Date().toISOString(),
    });

    if (this.timeout === null) {
      this.timeout = setTimeout(() => {
        this.flushBuffer();
        this.timeout = null;
      }, INTERVAL);
    }
  }

  trackKey(
    key: string,
    properties: {
      [key: string]: any;
    } = {}
  ) {
    this.track(
      'ui.event',
      Object.assign(
        {
          event_key: key,
        },
        decamelizeProperties(properties)
      )
    );
  }

  // FIXME contextName should be string but bad args are coming in
  tap(screenName: string, elementName: string, contextName: any = null, properties: Object = {}) {
    this.track(
      'ui.tapped',
      Object.assign(
        {
          screen_name: screenName,
          ui_element_name: elementName,
          ui_context: contextName,
        },
        decamelizeProperties(properties)
      )
    );
  }

  view(screenName: string | undefined, properties: Object = {}) {
    if (screenName) {
      this.track(
        'ui.viewed',
        Object.assign(
          {
            screen_name: screenName,
          },
          decamelizeProperties(properties)
        )
      );
    }
  }

  request(screenName: string, properties: Object = {}) {
    this.track(
      'ui.requested',
      Object.assign(
        {
          screen_name: screenName,
        },
        decamelizeProperties(properties)
      )
    );
  }

  experimentExposure(experiment: string, variant: string, properties: Object = {}) {
    this.track(
      'experiment.exposure',
      Object.assign(
        {
          experiment,
          alternative: variant,
        },
        decamelizeProperties(properties)
      )
    );
  }

  flushBuffer(): void {
    if (this.buffer.length === 0) {
      return;
    }

    const onError = (unsentEvents: EventData[]) => {
      this.buffer = this.buffer.concat(unsentEvents);
    };

    // shallow copy so that events added to the buffer during dispatch aren't sent twice
    const bufferCopy = [...this.buffer];
    this.buffer = [];
    try {
      dispatchBuffer(bufferCopy, this.getBatchProperties(), onError);
    } catch (err) {
      onError(bufferCopy);
      console.error('Failed to flushBuffer', err);
    }
  }
}

export default new EventTracking(ENABLED === 'true');

const stripHOCNames = (name: string) => {
  const thingsToRemove = [
    'Apollo',
    'awaitData',
    'FluxContainer',
    'Connect',
    'WaitForPromises',
    'TrackView',
    'withCookies',
    'withUserAgent',
    'withLayerHideOnEscapeTrigger',
    'withLayerHideOnTransitionTrigger',
    'withLayerAutoFocus',
    'withLayerHideOnBlurTrigger',
    'withDateTimePickerPosition',
    'withWidth',
    /[()-]/g,
  ];
  // @ts-ignore - initial conversion, no way to do this without changing code
  return thingsToRemove.reduce((res, thing) => res.replace(thing, ''), name);
};

export const getEventScreenName = (
  props:
    | {
        [key: string]: any;
      }
    | undefined,
  router: RouterState | undefined
): string | null => {
  if (props && props.eventScreenName) {
    return props.eventScreenName;
  }
  if (router) {
    const innermostRouteHandlerName = router.routes.reduce((prevName: any, route: any) => {
      if (route.component) {
        const { name, displayName } = route.component;
        return displayName || name || prevName;
      } else {
        return prevName;
      }
    }, '');

    if (innermostRouteHandlerName) {
      // @ts-ignore - initial conversion
      return stripHOCNames(innermostRouteHandlerName);
    }
  }
  return null;
};

export const getEventElementName = (props: { [key: string]: any }) => {
  return props.eventElementName || null;
};

export const getEventContextName = (
  props: {
    [key: string]: any;
  },
  context: {
    [key: string]: any;
  }
) => {
  if (props.eventContextName) {
    return props.eventContextName;
  }

  if (context.eventContextName) {
    return context.eventContextName;
  }

  return null;
};

export const getEventProperties = (
  eventProperties:
    | {
        [key: string]: any;
      }
    | undefined,
  context: {
    [key: string]: any;
  }
) => {
  return Object.assign(context.eventProperties || {}, eventProperties || {});
};
